/**
 * How an uploaded submittal's payload becomes a table.
 *
 * A data element whose `submissionMode` is `excel-upload` stores its payload as
 * an array of row-records — one per spreadsheet row — each shaped like the
 * schema's groups:
 *
 *   [ { project_details:  { project_contract_name: "BTO Project Delta 89", … },
 *       approver_details: { civil_and_structural_consultant_email: "…", … },
 *       component_listing:{ hdb_marking: "LPT", total_volume: 1, … } },
 *     … ]
 *
 * The groups are merged back into one flat row, which is why the project and
 * approver values repeat down the table: each uploaded row carried its own copy.
 *
 * Ported from pitstop-ui — fromSchemaShape/fromSchemaShapeAll in
 * src/pages/submittals/csv-schema.js, and fieldTitles/fieldFormats/formatHeader/
 * displayValue/isDateTimeFormat/formatIfDateTime/previewColumns in csv-display.js.
 * Both apps read the same payloads and the same element schemas, so the two copies
 * must agree on what a row is.
 */
import { formatDateTime } from '@/utils/formatDate'

/**
 * One stored record back to the flat row a table renders.
 *
 * Groups are merged rather than tracked, because a field name is unique across
 * the schema's groups. Anything the schema has no group for stays where it is.
 */
export const fromSchemaShape = (record, schema) => {
  const groups = new Set(Object.keys(schema?.properties ?? {}))
  const flat = {}

  Object.entries(record ?? {}).forEach(([key, value]) => {
    const isGroup = groups.has(key) && value && typeof value === 'object' && !Array.isArray(value)
    if (isGroup) {
      Object.assign(flat, value)
    } else {
      flat[key] = value
    }
  })

  return flat
}

/**
 * Every uploaded row, flattened.
 *
 * Anything that is not an array yields no rows: an object payload is the eform
 * shape, which this table cannot represent and must not half-render.
 */
export const fromSchemaShapeAll = (records, schema) =>
  (Array.isArray(records) ? records : []).map(record => fromSchemaShape(record, schema))

/**
 * Field name to its schema title. Names are unique across groups, so a flat
 * header matches on its own without knowing which group it came from.
 */
export const fieldTitles = schema => {
  const titles = {}
  Object.values(schema?.properties ?? {}).forEach(group => {
    Object.entries(group?.properties ?? {}).forEach(([name, field]) => {
      if (field?.title) titles[name] = field.title
    })
  })
  return titles
}

/** Each leaf's declared JSON-schema `format`, flattened the same way titles are. */
export const fieldFormats = schema => {
  const formats = {}
  Object.values(schema?.properties ?? {}).forEach(group => {
    Object.entries(group?.properties ?? {}).forEach(([name, field]) => {
      if (field?.format) formats[name] = field.format
    })
  })
  return formats
}

/**
 * An ISO date-time: a `T` and a time are both required.
 *
 * This is what keeps a month (`2026-05`) and a bare date (`2026-08-20`) out — both
 * parse fine, and formatting either as a timestamp invents a midnight that was
 * never in the data.
 */
const ISO_DATE_TIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/

/**
 * Does this field hold a timestamp?
 *
 * The precast schema declares `custom:date-time:YYYY-MM-DDTHH:mm:ssZ` on
 * `submission_date`, `c_s_approval_date` and `hdb_acknowledged_date`, and
 * `custom:date-time:YYYY-MM` on `expected_production_commencement_month`. The last
 * one is a submission month and is shown as authored, despite the `date-time` in
 * its name — so the pattern itself is what is tested, a day token and an hour token
 * both having to be present, rather than the `date-time` prefix. Date-only formats
 * are left alone for the same reason.
 */
export const isDateTimeFormat = format => {
  if (format === 'date-time') return true
  if (typeof format !== 'string' || !format.startsWith('custom:date-time:')) return false

  const pattern = format.slice('custom:date-time:'.length)
  return pattern.includes('DD') && /[Hh]/.test(pattern)
}

/**
 * A timestamp cell in the format the listings use; anything else untouched.
 *
 * A field the schema says nothing about falls back to the shape of its own value,
 * so the system-stamped columns still read correctly against a template whose
 * `format` keyword never made it into the schema.
 */
export const formatIfDateTime = (value, format) => {
  if (value === null || value === undefined) return value

  const isDate = format ? isDateTimeFormat(format) : ISO_DATE_TIME.test(String(value).trim())
  return isDate ? formatDateTime(value) : value
}

/** Fallback label for a header the schema does not name. */
export const formatHeader = key =>
  String(key)
    .replace(/([A-Z])/g, ' $1')
    .replaceAll('_', ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, character => character.toUpperCase())

/**
 * Cell text, with absent and blank values shown as a dash. A truthiness check
 * would also blank out `0` and `false`, which are meaningful here: a volume of 0,
 * an unticked thickness flag.
 */
export const displayValue = value => {
  if (value === null || value === undefined) return '-'
  const text = String(value).trim()
  return text === '' ? '-' : text
}

/** Is a cell blank — nothing the file supplied, as `displayValue` dashes it? */
const isBlank = value => value === null || value === undefined || String(value).trim() === ''

/**
 * Two present cells, ordered. Numeric where both sides parse as a number — so
 * "10" sorts after "9" rather than before it — a plain string compare
 * otherwise, which is what a timestamp needs: the stored ISO string already
 * reads in date order left to right.
 */
const compareCells = (a, b) => {
  if (a === b) return 0
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' })
}

/**
 * Rows sorted by one column's raw value — not the formatted cell text, so a
 * date column sorts chronologically rather than by whatever string
 * `formatIfDateTime` happened to print.
 *
 * Blanks sort last in both directions, same as Excel/Sheets — the direction
 * sign is applied to the comparison of two present values only, so a run of
 * dashes stays at the bottom rather than jumping to the top on a descending
 * sort.
 *
 * Client-side, matching the pagination above: the whole payload is already on
 * hand, so there is nothing to re-request. `key` null leaves the rows in the
 * order they were uploaded.
 */
export const sortRows = (rows, key, ascending) => {
  if (!key) return rows
  const direction = ascending ? 1 : -1
  return [...rows].sort((rowA, rowB) => {
    const a = rowA?.[key]
    const b = rowB?.[key]
    if (isBlank(a) && isBlank(b)) return 0
    if (isBlank(a)) return 1
    if (isBlank(b)) return -1
    return direction * compareCells(a, b)
  })
}

/**
 * Columns in the file's own order, labelled from the schema. An unrecognised
 * header is kept rather than dropped, so a template mismatch is visible rather
 * than silently trimmed away.
 *
 * Returns the shape MasterDataTable's `columns` prop takes. Every column is
 * sortable — the rows carry no meaningful order of their own once uploaded, and
 * a reviewer needs to group them by any field to check them.
 */
export const uploadColumns = (headers, schema) => {
  const titles = fieldTitles(schema)
  const formats = fieldFormats(schema)
  return (headers ?? [])
    .filter(header => typeof header === 'string' && header.trim())
    .map(header => ({
      key: header,
      label: titles[header] || formatHeader(header),
      sortable: true,
      // Timestamps read as the listings write them rather than as the stored ISO
      // string. The month column is deliberately untouched — see isDateTimeFormat.
      render: row => displayValue(formatIfDateTime(row?.[header], formats[header])),
      // Every column the same, rather than MasterDataTable's per-key widths:
      // those name EMS's columns, and a spreadsheet that happens to carry a
      // `project_contract_name` field would otherwise get EMS's 320px for it.
      //
      // Shrink 0, so the row overflows and the table scrolls sideways once the
      // columns no longer fit. An uploaded element carries as many columns as
      // its spreadsheet has — precast has fourteen — and letting them shrink to
      // share one screen gives every column about 120px, too narrow for the
      // values they hold. Grow 1 so a narrow upload still fills the card rather
      // than trailing off with empty space.
      style: { flex: '1 0 180px', minWidth: '180px' },
    }))
}

/** The column order for a set of rows: the first row's keys, as uploaded. */
export const uploadHeaders = rows => Object.keys(rows?.[0] ?? {})
